
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useNotify } from '@/hooks/useNotify';
import { useAuth } from '@/contexts/AuthContext';
import { useImpersonationGuard } from '@/hooks/useImpersonationGuard';

export interface DocumentDefaults {
  id?: string;
  company_id: string;
  default_payment_terms?: string;
  default_payment_days?: number;
  default_deposit_percentage?: number;
  final_payment_terms?: string;
  estimate_email_template?: string;
  invoice_email_template?: string;
  estimate_email_subject?: string;
  invoice_email_subject?: string;
  estimate_footer?: string;
  invoice_footer?: string;
  default_special_instructions?: string;
  default_contract_terms?: string;
  warranty_terms?: string;
  other_standard_contract_terms?: string;
  credit_note_footer?: string;
  standard_contract_pdf_url?: string;
  material_delivery_terms?: string;
  change_order_approval_process?: string;
  safety_requirements_text?: string;
  insurance_requirements?: string;
  permit_requirements_text?: string;
  cleanup_procedures?: string;
  quality_assurance_terms?: string;
  estimate_validity_days?: number;
  invoice_due_days?: number;
  email_signature_template?: string;
}

export const useDocumentDefaults = () => {
  const [documentDefaults, setDocumentDefaults] = useState<DocumentDefaults | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const notify = useNotify();
  const { user, profile } = useAuth();
  const { guardWrite } = useImpersonationGuard();

  useEffect(() => {
    if (user && profile?.company_id) {
      fetchDocumentDefaults();
      checkAdminStatus();
    }
  }, [user, profile]);

  const checkAdminStatus = async () => {
    if (!user) return;

    try {
      const { data: memberData } = await supabase
        .from('company_members')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .eq('status', 'active')
        .maybeSingle();

      setIsAdmin(!!memberData);
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
    }
  };

  const fetchDocumentDefaults = async () => {
    if (!profile?.company_id) return;

    try {
      const { data, error } = await supabase
        .from('document_defaults')
        .select('*')
        .eq('company_id', profile.company_id)
        .maybeSingle();

      if (error) {
        throw error;
      }

      setDocumentDefaults(data);
    } catch (error) {
      console.error('Error fetching document defaults:', error);
      notify.error(error);
    } finally {
      setLoading(false);
    }
  };

  const saveDocumentDefaults = async (data: Partial<DocumentDefaults>) => {
    if (!guardWrite('edit document defaults')) return false;
    if (!profile?.company_id || !isAdmin) {
      notify.error("Only admins can save document defaults");
      return false;
    }

    setIsSaving(true);
    try {
      const defaultsData = {
        ...data,
        company_id: profile.company_id,
      };

      if (documentDefaults?.id) {
        // Update existing
        const { error } = await supabase
          .from('document_defaults')
          .update(defaultsData)
          .eq('id', documentDefaults.id);

        if (error) throw error;
      } else {
        // Create new
        const { data: newData, error } = await supabase
          .from('document_defaults')
          .insert([defaultsData])
          .select()
          .single();

        if (error) throw error;
        setDocumentDefaults(newData);
      }

      await fetchDocumentDefaults();
      return true;
    } catch (error) {
      console.error('Error saving document defaults:', error);
      notify.error(error);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return {
    documentDefaults,
    loading,
    isAdmin,
    isSaving,
    saveDocumentDefaults,
    refetch: fetchDocumentDefaults,
  };
};
